import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard from 'utils/route-guard/AuthGuard';
import RequireAdminAccess from 'utils/route-guard/RequireAdminAccess';
import RequireModuleAccess from 'utils/route-guard/RequireModuleAccess';

// Lazy jako všechno ostatní zde (2026-08-30, na výslovné přání uživatele -
// optimalizace prvotního načtení) - dřív byl RouteError jediný eagerně
// importovaný komponent v celém souboru, což táhlo AnimateButton (a s ním
// framer-motion) do sdíleného chunku úplně zbytečně, protože chybová
// stránka se reálně vykreslí jen výjimečně.
const RouteError = Loadable(lazy(() => import('pages/error/RouteError')));

// render- Dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));

// render - Docházka
const AttendanceClock = Loadable(lazy(() => import('pages/attendance/Clock')));
const AttendanceActivity = Loadable(lazy(() => import('pages/attendance/Activity')));
const AttendanceLeaveRequests = Loadable(lazy(() => import('pages/attendance/LeaveRequests')));
const AttendancePropustkaRequest = Loadable(lazy(() => import('pages/attendance/PropustkaRequest')));
const AttendanceLeaveRequestVacation = Loadable(lazy(() => import('pages/attendance/LeaveRequestVacation')));
const AttendanceLeaveRequestUnpaid = Loadable(lazy(() => import('pages/attendance/LeaveRequestUnpaid')));
const AttendanceDashboardEmployeeDocumentation = Loadable(lazy(() => import('pages/attendance/dashboard/EmployeeDocumentation')));
const AttendanceDashboardRegionalManager = Loadable(lazy(() => import('pages/attendance/dashboard/RegionalManager')));
const AttendanceDashboardRegionalManagerDocumentation = Loadable(
  lazy(() => import('pages/attendance/dashboard/RegionalManagerDocumentation'))
);
const AttendanceDashboardPayroll = Loadable(lazy(() => import('pages/attendance/dashboard/Payroll')));
const AttendanceDashboardTechnicalDocumentation = Loadable(lazy(() => import('pages/attendance/dashboard/TechnicalDocumentation')));
const AttendanceDashboardPayrollDocumentation = Loadable(lazy(() => import('pages/attendance/dashboard/PayrollDocumentation')));

// render - Docházkový systém (manažer)
const AttendanceSickLeaveManager = Loadable(lazy(() => import('pages/attendance/manager/SickLeaveManager')));
const AttendanceFamilyCareManager = Loadable(lazy(() => import('pages/attendance/manager/FamilyCareManager')));
const AttendanceObstacleManager = Loadable(lazy(() => import('pages/attendance/manager/ObstacleManager')));
const AttendanceApprovalsManager = Loadable(lazy(() => import('pages/attendance/manager/ApprovalsManager')));
const AttendancePropustkaRequestsManager = Loadable(lazy(() => import('pages/attendance/manager/PropustkaRequestsManager')));
const AttendanceAreasManager = Loadable(lazy(() => import('pages/attendance/manager/AreasManager')));
const AttendanceMonthlyApprovalsManager = Loadable(lazy(() => import('pages/attendance/manager/MonthlyApprovalsManager')));
const AttendanceEmploymentPeriodsManager = Loadable(lazy(() => import('pages/attendance/manager/EmploymentPeriodsManager')));
const AttendanceJobTitleMinimumsManager = Loadable(lazy(() => import('pages/attendance/manager/JobTitleMinimumsManager')));
const AttendancePropustkaReasonsManager = Loadable(lazy(() => import('pages/attendance/manager/PropustkaReasonsManager')));
const AttendanceLeaveEntitlementsManager = Loadable(lazy(() => import('pages/attendance/manager/LeaveEntitlementsManager')));
const AttendancePendingClassificationManager = Loadable(lazy(() => import('pages/attendance/manager/PendingClassificationManager')));
const AttendanceTeamPropustky = Loadable(lazy(() => import('pages/attendance/manager/TeamPropustky')));
const AttendancePayrollExport = Loadable(lazy(() => import('pages/attendance/manager/PayrollExport')));
const AttendanceVerificationReasonsManager = Loadable(lazy(() => import('pages/attendance/manager/VerificationReasonsManager')));

// render - Admin
const AdminAuditWhitelist = Loadable(lazy(() => import('pages/admin/AuditWhitelist')));
const AdminUsers = Loadable(lazy(() => import('pages/admin/Users')));
const AdminRoles = Loadable(lazy(() => import('pages/admin/Roles')));
const AdminGroups = Loadable(lazy(() => import('pages/admin/Groups')));
const AdminModules = Loadable(lazy(() => import('pages/admin/Modules')));
const AdminPush = Loadable(lazy(() => import('pages/admin/Push')));
const AdminTenants = Loadable(lazy(() => import('pages/admin/Tenants')));
const AdminTrustedNetworks = Loadable(lazy(() => import('pages/admin/TrustedNetworks')));
const AdminSecurityMeasures = Loadable(lazy(() => import('pages/admin/SecurityMeasures')));
const AdminPermissionsGuide = Loadable(lazy(() => import('pages/admin/PermissionsGuide')));
const AdminMessagesGuide = Loadable(lazy(() => import('pages/admin/MessagesGuide')));
const AdminEmailGuide = Loadable(lazy(() => import('pages/admin/EmailGuide')));
const AdminAttendanceGuide = Loadable(lazy(() => import('pages/admin/AttendanceGuide')));

// ==============================|| MAIN ROUTING ||============================== //
// Demo pages (component-overview, sample-page) removed along with their menu
// entries - each real module adds its own route as it's built.
//
// RequireModuleAccess (2026-08-22, "core_modules" katalog) replaced the old
// hardcoded RequireSystemAdmin - moduleKey/minRole here must match the
// corresponding menu-items/admin.jsx entry's `access` exactly, otherwise a
// menu item would be visible/hidden inconsistently with what its own route
// actually allows.

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <DashboardLayout />
    </AuthGuard>
  ),
  errorElement: <RouteError />,
  children: [
    {
      path: '/',
      element: <DashboardDefault />
    },
    {
      path: 'dashboard',
      children: [
        {
          // Holé /dashboard (bez /default) reálný bug (2026-08-23) - dřív
          // tahle úroveň neměla vlastní index element, takže se vykreslil
          // jen layout (navbar/sidebar/patička) a <Outlet /> zůstal
          // prázdný - vypadalo to jako by se stránka po refreshi vůbec
          // nenačetla. APP_DEFAULT_PATH sice cílí na /dashboard/default,
          // ale bez tohohle indexu byla holá /dashboard mrtvá cesta.
          index: true,
          element: <DashboardDefault />
        },
        {
          path: 'default',
          element: <DashboardDefault />
        }
      ]
    },
    {
      // "Docházkové karty" (2026-09-01, na výslovné přání uživatele -
      // "zatím jen manažeři") - dřív minRole="reader" (kdokoliv s přístupem
      // k Docházce), teď omezeno na manažery. Odkaz v menu je od
      // 2026-09-02 v buildAttendanceDashboardMenu()'s sekci "Oblastní
      // manažer" (menu-items/attendance.jsx).
      path: 'attendance',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceClock />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/leave-requests',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="reader">
          <AttendanceLeaveRequests />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/activity',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="reader">
          <AttendanceActivity />
        </RequireModuleAccess>
      )
    },
    {
      // "Žádanka na propustku" (2026-09-01, na výslovné přání uživatele) -
      // menu-items/attendance.jsx's "Zaměstnanec" sekce, gatuje jen na
      // 'reader' jako zbytek téhle sekce.
      path: 'attendance/propustky/zadanka',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="reader">
          <AttendancePropustkaRequest />
        </RequireModuleAccess>
      )
    },
    {
      // "Žádost o dovolenou" (2026-09-04, na výslovné přání uživatele -
      // rozdělení z dřívější kombinované žádosti na dva samostatné odkazy)
      // - menu-items/attendance.jsx's "Zaměstnanec" sekce, gatuje jen na
      // 'reader' jako zbytek téhle sekce.
      path: 'attendance/dovolena/zadost',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="reader">
          <AttendanceLeaveRequestVacation />
        </RequireModuleAccess>
      )
    },
    {
      // "Žádost o neplacené volno" (2026-09-04, sourozenec žádosti výše).
      path: 'attendance/neplacene-volno/zadost',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="reader">
          <AttendanceLeaveRequestUnpaid />
        </RequireModuleAccess>
      )
    },
    {
      // "Docházkový systém" launcher (2026-09-01, na výslovné přání
      // uživatele) - menu-items/attendance.jsx's buildAttendanceDashboardMenu().
      // "Zaměstnanec" gatuje jen na 'reader' (kdokoliv s přístupem k
      // Docházce) - ostatní dvě "Řídicí panel" stránky gatují stejně jako
      // "Oblastní manažer" - "Mzdová účetní" je podle AttendanceGuide.jsx
      // táž role regional_manager, jen bez scope_group_id omezení na jednu
      // pobočku.
      path: 'attendance/dashboard/employee-documentation',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="reader">
          <AttendanceDashboardEmployeeDocumentation />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/dashboard/regional-manager',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceDashboardRegionalManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/dashboard/regional-manager-documentation',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceDashboardRegionalManagerDocumentation />
        </RequireModuleAccess>
      )
    },
    {
      // requirePayrollGrant (2026-09-01, na výslovné přání uživatele - "do
      // sekce mzdová účetní nemá krom její role a admin další přístup") -
      // minRole="regional_manager" samo o sobě by pustilo i vedoucí
      // pobočky (stejná role, jen scoped) - viz RequireModuleAccess.jsx.
      path: 'attendance/dashboard/payroll',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager" requirePayrollGrant>
          <AttendanceDashboardPayroll />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/dashboard/payroll-documentation',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager" requirePayrollGrant>
          <AttendanceDashboardPayrollDocumentation />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/dashboard/technical-documentation',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="admin">
          <AttendanceDashboardTechnicalDocumentation />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/sick-leave',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceSickLeaveManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/family-care',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceFamilyCareManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/obstacle',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceObstacleManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/approvals',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceApprovalsManager />
        </RequireModuleAccess>
      )
    },
    {
      // "Žádanky na propustku" (2026-09-01, na výslovné přání uživatele) -
      // schvalování řeší i vedoucí pobočky, ne jen mzdová účetní - proto
      // BEZ requirePayrollGrant (na rozdíl od propustka-reasons katalogu).
      path: 'attendance/manager/propustka-requests',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendancePropustkaRequestsManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/areas',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceAreasManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/monthly-approvals',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceMonthlyApprovalsManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/employment-periods',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceEmploymentPeriodsManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/job-title-minimums',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceJobTitleMinimumsManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/propustka-reasons',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager" requirePayrollGrant>
          <AttendancePropustkaReasonsManager />
        </RequireModuleAccess>
      )
    },
    {
      // requirePayrollGrant (2026-09-01, na výslovné přání uživatele -
      // "uprav styl a oprávnění jako při nastavení propustky") - zápis
      // (set()) je na backendu omezený na mzdovou účetní (ATT-403-007),
      // stejně jako katalog důvodů propustky.
      path: 'attendance/manager/leave-entitlements',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager" requirePayrollGrant>
          <AttendanceLeaveEntitlementsManager />
        </RequireModuleAccess>
      )
    },
    {
      path: 'attendance/manager/verification-reasons',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceVerificationReasonsManager />
        </RequireModuleAccess>
      )
    },
    {
      // "Neplánovaná zdravotní absence" (2026-09-01, Technická dokumentace
      // bod 5) - BEZ requirePayrollGrant, řeší kterýkoliv manažer ve svém
      // rozsahu (backend: AttendancePropustkaController::pending()/resolve(),
      // min. 'regional_manager', ne payroll-only).
      path: 'attendance/manager/pending-classification',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendancePendingClassificationManager />
        </RequireModuleAccess>
      )
    },
    {
      // "Přehled propustek týmu" (2026-09-04) - BEZ requirePayrollGrant,
      // stejný scoping vzor jako Neplánovaná zdravotní absence výše
      // (backend: AttendancePropustkaController::team(), min.
      // 'regional_manager'). Náhled přílohy samotné je uvnitř stránky dál
      // omezený na attendance_payroll_access, viz TeamPropustky.jsx.
      path: 'attendance/manager/team-propustky',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager">
          <AttendanceTeamPropustky />
        </RequireModuleAccess>
      )
    },
    {
      // requirePayrollGrant - zápis... vlastně jen čtení, ale backend
      // (ATT-403-008) je omezené na mzdovou účetní stejně jako ostatní
      // mzdové nástroje.
      path: 'attendance/manager/payroll-export',
      element: (
        <RequireModuleAccess moduleKey="attendance" minRole="regional_manager" requirePayrollGrant>
          <AttendancePayrollExport />
        </RequireModuleAccess>
      )
    },
    {
      path: 'admin',
      children: [
        {
          path: 'registration',
          element: (
            <RequireAdminAccess>
              <AdminAuditWhitelist />
            </RequireAdminAccess>
          )
        },
        {
          path: 'users',
          element: (
            <RequireAdminAccess>
              <AdminUsers />
            </RequireAdminAccess>
          )
        },
        {
          path: 'roles',
          element: (
            <RequireAdminAccess>
              <AdminRoles />
            </RequireAdminAccess>
          )
        },
        {
          path: 'groups',
          element: (
            <RequireAdminAccess>
              <AdminGroups />
            </RequireAdminAccess>
          )
        },
        {
          path: 'modules',
          element: (
            <RequireAdminAccess>
              <AdminModules />
            </RequireAdminAccess>
          )
        },
        {
          path: 'push',
          element: (
            <RequireModuleAccess moduleKey="push" minRole="editor">
              <AdminPush />
            </RequireModuleAccess>
          )
        },
        {
          path: 'tenants',
          element: (
            <RequireModuleAccess moduleKey="system" minRole="admin">
              <AdminTenants />
            </RequireModuleAccess>
          )
        },
        {
          path: 'trusted-networks',
          element: (
            <RequireModuleAccess moduleKey="system" minRole="admin">
              <AdminTrustedNetworks />
            </RequireModuleAccess>
          )
        },
        {
          path: 'docs',
          children: [
            {
              path: 'security',
              element: (
                <RequireAdminAccess>
                  <AdminSecurityMeasures />
                </RequireAdminAccess>
              )
            },
            {
              path: 'permissions',
              element: (
                <RequireAdminAccess>
                  <AdminPermissionsGuide />
                </RequireAdminAccess>
              )
            },
            {
              path: 'messages',
              element: (
                <RequireModuleAccess moduleKey="push" minRole="reader">
                  <AdminMessagesGuide />
                </RequireModuleAccess>
              )
            },
            {
              path: 'email',
              element: (
                <RequireModuleAccess moduleKey="system" minRole="admin">
                  <AdminEmailGuide />
                </RequireModuleAccess>
              )
            },
            {
              path: 'attendance',
              element: (
                <RequireModuleAccess moduleKey="attendance" minRole="reader">
                  <AdminAttendanceGuide />
                </RequireModuleAccess>
              )
            }
          ]
        }
      ]
    },
    {
      // Allowlist catch-all (2026-08-26) - any path not explicitly listed
      // above (typo'd URL, removed route, probing) lands here instead of
      // React Router's raw "No routes matched location" error (which used
      // to fall through to MainRoutes' errorElement/RouteError - a generic
      // "Něco se pokazilo" screen, not the required redirect). Sits inside
      // AuthGuard, so an unauthenticated visitor still hits /login first;
      // this only fires for an authenticated user on an unknown path.
      // Server-side authorization (RequirePermissionMiddleware et al. in
      // apps/do-ra.cz/api) is the actual security boundary regardless of
      // what this route does - this is UX/defense-in-depth only.
      path: '*',
      element: <Navigate to="/dashboard" replace />
    }
  ]
};

export default MainRoutes;
